// src/tenantAdmin/components/RunDetailModal.jsx
// Quick-look detail for a run, opened from the "Storico Generazioni" list.
// Left = the brief that was submitted (INPUT), right = the generated text (OUTPUT).
// The OUTPUT column shows the WHOLE output — every piece of the entry as plain
// text (utils/formatters.js :: getFullOutputTextForEntry → publish/serialize/
// plain.js), one "=== label ===" block per piece — not only the paste-ready
// primary piece: a quick look must show hashtags, first comment, meta fields,
// FAQ too. Paragraphs (blank-line separated) render as stacked boxes.
// "Modifica" reuses the same Content-Generator handoff as RunDetailPage
// (preloadedBrief + a synthetic history entry matching useGeneration's shape).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { getRunDetail } from '../../admin/runsApi.js';
import { briefEnvelopeToFormState } from '../../utils/briefEnvelopeToFormState.js';
import { resultsFromRunDetail } from '../../utils/runResults.js';
import { getFullOutputTextForEntry } from '../../utils/formatters.js';
import { getModeConfig } from '../../constants/modes.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { useContentInset } from '../../components/ui/modalArea.js';
import RunStatusPill from './RunStatusPill.jsx';

const label = {
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase',
  color: COLORS.textTertiary, fontFamily: FONT_SANS, marginBottom: '6px',
};
const value = {
  fontSize: '13px', color: COLORS.ink, fontFamily: FONT_SANS, lineHeight: 1.5,
};
const btnBase = {
  height: '44px', padding: '0 24px', borderRadius: RADII.control,
  fontSize: '14px', fontWeight: 600, fontFamily: FONT_SANS, cursor: 'pointer',
};

export default function RunDetailModal({ taskId, onClose }) {
  // Centred in the page's content area (beside the sidebar), like every app modal — components/ui/modalArea.js.
  const insetLeft = useContentInset(true);
  const navigate = useNavigate();
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!taskId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetail(null);
    getRunDetail(taskId)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(e => { if (!cancelled) setError(e.message || 'Errore nel caricamento della generazione'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [taskId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Same synthetic history entry RunDetailPage builds — mirrors the shape
  // produced by useGeneration.executeGeneration.
  const synthEntry = useMemo(() => {
    if (!detail || !detail.result_json) return null;
    const fe = detail.brief_json ? briefEnvelopeToFormState(detail.brief_json) : {};
    const activeMode = fe.activeMode || detail.platform;
    return {
      id: detail.task_id,
      timestamp: detail.completed_at ? new Date(detail.completed_at) : new Date(detail.created_at),
      mode: activeMode,
      modeConfig: getModeConfig(activeMode),
      results: resultsFromRunDetail(detail),
      params: detail.brief_json
        ? { ...detail.brief_json, activeMode, socialChannel: fe.socialChannel, emailChannel: fe.emailChannel }
        : null,
      generationDuration: null,
    };
  }, [detail]);

  // Split the generated text into paragraphs so it renders as the stacked boxes.
  const outputBlocks = useMemo(() => {
    if (!synthEntry) return [];
    const text = getFullOutputTextForEntry(synthEntry) || '';
    return text.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
  }, [synthEntry]);

  const brief = detail?.brief_json;

  const handleModifica = useCallback(() => {
    if (!brief) return;
    navigate('/content-generator', {
      state: { preloadedBrief: brief, preloadedHistoryEntry: synthEntry },
    });
  }, [brief, synthEntry, navigate]);

  const created = detail?.created_at
    ? new Date(detail.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
    : null;
  const subtitle = detail
    ? [
        `${detail.platform || '—'}${detail.content_format ? ` / ${detail.content_format}` : ''}`,
        created,
        detail.task_id ? String(detail.task_id).slice(-8) : null,
      ].filter(Boolean).join(' · ')
    : '';

  const keyPoints = Array.isArray(brief?.key_points) ? brief.key_points.filter(Boolean) : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(35,0,62,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', fontFamily: FONT_SANS,
        ...(insetLeft ? { paddingLeft: `${insetLeft + 24}px` } : null),
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Dettaglio generazione"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '840px', maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column',
          background: COLORS.surface, borderRadius: '28px',
          boxShadow: '0 24px 64px rgba(35,0,62,0.28)',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <div style={{ padding: '48px', textAlign: 'center', color: COLORS.textTertiary, fontSize: '13px' }}>
            Caricamento…
          </div>
        )}
        {!loading && error && (
          <div style={{ padding: '48px', textAlign: 'center', color: SEMANTIC.errore.fg, fontSize: '13px' }}>{error}</div>
        )}
        {!loading && !error && !detail && (
          <div style={{ padding: '48px', textAlign: 'center', color: COLORS.textTertiary, fontSize: '13px' }}>
            Generazione non trovata
          </div>
        )}

        {!loading && !error && detail && (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '24px 28px 20px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
                border: `1px solid ${COLORS.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileText size={20} color={COLORS.ink} strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '17px', fontWeight: 700, color: COLORS.ink, lineHeight: 1.35 }}>
                  {detail.topic || '—'}
                </div>
                <div style={{ fontSize: '12px', color: COLORS.textTertiary, marginTop: '4px' }}>
                  {subtitle}
                </div>
              </div>
              <RunStatusPill status={detail.status} />
            </div>

            {/* Body — INPUT | OUTPUT */}
            <div style={{
              flex: 1, overflowY: 'auto',
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: '28px', padding: '0 28px 24px',
            }}>
              {/* INPUT */}
              <div style={{ borderRight: `1px solid ${COLORS.border}`, paddingRight: '28px' }}>
                <div style={{ ...label, marginBottom: '18px' }}>Input</div>

                <div style={{ marginBottom: '18px' }}>
                  <div style={label}>Argomento</div>
                  <div style={value}>{brief?.topic || detail.topic || '—'}</div>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <div style={label}>Punti chiave</div>
                  <div style={value}>{keyPoints.length ? keyPoints.join(', ') : '—'}</div>
                </div>

                <div>
                  <div style={label}>Indicazioni editoriali</div>
                  <div style={value}>{brief?.additional_context || '—'}</div>
                </div>
              </div>

              {/* OUTPUT */}
              <div>
                <div style={{ ...label, marginBottom: '18px' }}>Output</div>
                {outputBlocks.length === 0 ? (
                  <div style={{ ...value, color: COLORS.textTertiary }}>Nessun contenuto generato</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {outputBlocks.map((block, i) => (
                      <div
                        key={i}
                        style={{
                          background: COLORS.field,
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: RADII.control,
                          padding: '14px 16px',
                          fontSize: '13px', lineHeight: 1.6, color: COLORS.ink,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {block}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '12px',
              padding: '18px 28px', borderTop: `1px solid ${COLORS.border}`,
            }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  ...btnBase,
                  background: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.ink,
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleModifica}
                disabled={!brief}
                title={!brief ? 'Brief non disponibile per questa generazione' : undefined}
                style={{
                  ...btnBase,
                  background: COLORS.accent,
                  border: 'none',
                  color: COLORS.surface,
                  opacity: brief ? 1 : 0.5,
                  cursor: brief ? 'pointer' : 'not-allowed',
                }}
              >
                Modifica
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
